"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useRef } from "react"
import { useCookieConsent } from "./consent-context"
import { GA4_MEASUREMENT_ID } from "./tracking-scripts"

// Sends a GA4 `page_view` on every client-side navigation.
//
// gtag.js only fires a page_view automatically when it initialises on a full
// page load (via the `gtag('config', …)` call in tracking-scripts.tsx). In an
// App Router SPA, link clicks navigate client-side with no reload, so without
// this component GA4 would only ever record the entry page (typically the
// homepage). Here we re-send page_view whenever the path or query changes.
function Tracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { consent } = useCookieConsent()

  // Read consent at fire-time via a ref so granting consent mid-session does
  // not itself trigger a page_view — the gtag `config` call already counts the
  // page the visitor consented on. We only react to actual route changes.
  const analyticsRef = useRef(false)
  analyticsRef.current = consent?.analytics === true

  // Skip the first render: the entry page is already counted by gtag's own
  // automatic page_view on load, so sending here too would double-count it.
  const isFirst = useRef(true)

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false
      return
    }
    if (!analyticsRef.current) return

    const w = window as typeof window & { gtag?: (...args: unknown[]) => void }
    if (typeof w.gtag !== "function") return

    const query = searchParams?.toString()
    const page_path = query ? `${pathname}?${query}` : pathname

    w.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA4_MEASUREMENT_ID,
    })
    // analyticsRef is intentionally excluded — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams])

  return null
}

export default function GA4PageView() {
  return <Tracker />
}
